import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";

/** Upload before mutating the canvas. Never persist a revocable browser blob URL. */
export async function prepareImportedMap(
  map: MapCanvasValue,
  png: Blob | undefined,
  upload: (file: File) => Promise<string>,
  onProgress?: (completed: number, total: number) => void
): Promise<MapCanvasValue> {
  const files = new Map<string, File>();
  if (png)
    files.set(
      "background",
      new File([png], "floorplan-reference.png", { type: "image/png" })
    );
  for (const shape of map.shapes) {
    if (
      shape.type !== "furniture" ||
      !shape.furniture.url.startsWith("data:image/svg+xml,")
    )
      continue;
    const source = shape.furniture.url;
    if (!files.has(source))
      files.set(
        source,
        new File(
          [decodeURIComponent(source.slice("data:image/svg+xml,".length))],
          `floorplan-furniture-${files.size}.svg`,
          { type: "image/svg+xml" }
        )
      );
  }
  const jobs = [...files];
  const urls = new Map<string, string>();
  let next = 0,
    completed = 0;
  let failure: unknown;
  onProgress?.(0, jobs.length);
  // Bound network pressure; wait for in-flight uploads before reporting failure.
  await Promise.all(
    Array.from({ length: Math.min(4, jobs.length) }, async () => {
      while (!failure && next < jobs.length) {
        const [key, file] = jobs[next++];
        try {
          const url = await upload(file);
          if (!/^https?:\/\//.test(url))
            throw new Error("The asset upload did not return a persistent URL");
          urls.set(key, url);
          onProgress?.(++completed, jobs.length);
        } catch (error) {
          failure =
            error instanceof Error
              ? error
              : new Error("Map asset upload failed");
        }
      }
    })
  );
  if (failure) throw failure;
  const path = urls.get("background");
  const shapes = map.shapes
    .filter((shape) => !png || shape.type !== "zone")
    .map((shape) => {
      if (shape.type !== "furniture" || !urls.has(shape.furniture.url))
        return shape;
      return {
        ...shape,
        furniture: { ...shape.furniture, url: urls.get(shape.furniture.url)! }
      };
    });
  if (!path) return { ...map, shapes };
  return {
    ...map,
    shapes: [
      {
        id: crypto.randomUUID(),
        type: "image",
        layerType: "background",
        layerLabel: "Original floorplan",
        locked: true,
        x: 0,
        y: 0,
        width: map.editingZoneWidth,
        height: map.editingZoneHeight,
        path
      },
      ...shapes
    ]
  };
}
